import { AExamEnterProcess, AExamRecord } from 'kuaidong-api'
import { AppUtil, SchoolUtil, StringUtil } from 'kuaidong-lib'
import {
  IExamRecord,
  IJumpropeIntelligentClazz,
  ISExamStudentInfo,
  IJumpropeIntelligentStudent,
  Gender,
} from 'kuaidong-model'
import { ClassService } from 'kuaidong-service'
import { jumpropeIntelligentStore } from 'kuaidong-store'

export class TiceService {
  public static NewTiceId = '-1' // 标识用于新建体测记录
  public static JumpRopeItemField = 'jump_rope' // 用于跳绳的字段

  public static currentWorkingCamp: {
    examRecord: IExamRecord
    clazzs: IJumpropeIntelligentClazz[]
    currentClazz: IJumpropeIntelligentClazz | null
    students: (IJumpropeIntelligentStudent & { folded: boolean; selected: boolean })[]
  } | null = null

  // 获得体测上传列表
  public static async getList(): Promise<IExamRecord[]> {
    const result = await AExamRecord.all()
    return result.items
  }

  public static async initWorkingCamp(examRecord: IExamRecord) {
    const clazzs = await this.getClazzList(examRecord.id)
    clazzs.forEach((item) => {
      item.id = item.class_id
    })
    let students: ISExamStudentInfo[] = []
    if (clazzs.length > 0) {
      students = await this.getJumpropeStudents(examRecord.id, clazzs[0])
    }
    this.currentWorkingCamp = {
      examRecord: examRecord,
      clazzs: clazzs,
      currentClazz: clazzs.length ? clazzs[0] : null,
      students: students.map((item) => this.buildIJumpropeIntelligentStudent(item)),
    }
  }

  public static async updateWorkingCampClazz(clazzId: number) {
    if (this.currentWorkingCamp) {
      const currentClazz = this.currentWorkingCamp!.clazzs.find((item) => item.id === clazzId)
      if (currentClazz) {
        const students = await this.getJumpropeStudents(this.currentWorkingCamp!.examRecord.id, currentClazz!)
        this.currentWorkingCamp.currentClazz = currentClazz
        this.currentWorkingCamp.students = students.map((item) => this.buildIJumpropeIntelligentStudent(item))
        return true
      }
    }
    throw new Error('没有找到对应的班级')
  }

  // 把最佳成绩替换掉, 用于后续展示
  public static updateStudentScores(finishedStudents: IJumpropeIntelligentStudent[]) {
    const finishedStduentMap = new Map(finishedStudents.map((obj) => [obj.student_enrollment_no, obj]))
    if (this.currentWorkingCamp && this.currentWorkingCamp.students) {
      for (const student of this.currentWorkingCamp!.students) {
        if (finishedStduentMap.has(student.student_enrollment_no)) {
          student.best_jump_count = finishedStduentMap.get(student.student_enrollment_no)!.best_jump_count
        }
      }
    }
  }

  // 获得对应体测的班级列表
  public static async getClazzList(
    ticeRecordId: string
  ): Promise<(IJumpropeIntelligentClazz & { class_id: number })[]> {
    const result: (IJumpropeIntelligentClazz & { class_id: number; class_name: string })[] = await AppUtil.httpGET(
      '/cel/body_exam/stat/item_grade_class_stat_summary/jump_rope',
      {
        upload_score_id: ticeRecordId,
      }
    )
    for (const item of result) {
      item.jump_completed_student_count = 0
      item.jump_uncompleted_student_count = 0
      item.name = item.class_name
    }
    return result
  }

  public static async createStudent(studentInfo: any) {
    if (!this.currentWorkingCamp) throw new Error('当前没有工作数据')
    return AExamRecord.createStudent({
      upload_score_id: this.currentWorkingCamp.examRecord.id,
      items: studentInfo,
    })
  }

  // 新建体测记录
  public static async createExamRecord(): Promise<IExamRecord> {
    return await AppUtil.httpPOST('/v2/device/upload_score')
  }

  public static async checkExamRecordHasStudentCode(upload_score_id: string) {
    return await AppUtil.httpGET('/v2/device/student_code_list', {
      upload_score_id,
    })
  }

  public static async loadStudentsFromNameList(upload_score_id: string) {
    return await AppUtil.httpPOST('/v2/device/student_code_list', {
      upload_score_id,
    })
  }

  public static async getJumpropeStudents(
    ticeRecordId: string,
    ticeClazz: IJumpropeIntelligentClazz
  ): Promise<ISExamStudentInfo[]> {
    const students = await AppUtil.httpGET('/v2/device/item_grade_class_student_stat', {
      upload_score_id: ticeRecordId,
      item_field: this.JumpRopeItemField,
      grade_no: ticeClazz.grade_no,
      class_no: ticeClazz.class_no,
    })
    return students.map((student: any) => ({
      ...student,
      projects: [{ field: this.JumpRopeItemField, score: student.score }],
    }))
  }

  public static buildIJumpropeIntelligentStudent(
    student: ISExamStudentInfo
  ): IJumpropeIntelligentStudent & { folded: boolean; selected: boolean } {
    return {
      gender: student.gender === '男' ? Gender.Male : Gender.Female,
      full_name: student.name,
      best_jump_count: student.score ? parseInt(student.score) : 0,
      student_enrollment_no: student.school_roll_no,
      jump_record_list: [],
      folded: false,
      selected: false,
    }
  }

  public static clearCurrentData() {
    jumpropeIntelligentStore.reset()
    this.currentWorkingCamp = null
  }

  public static getCurrentWorkingGradeNos(): number[] {
    const gradeSet = new Set<number>()
    if (this.currentWorkingCamp) {
      this.currentWorkingCamp!.clazzs.forEach((item) => {
        gradeSet.add(item.grade_no)
      })
    }
    return Array.from(gradeSet.values()).sort()
  }

  // 构建年级下拉列表
  public static buildCurrentWorkingGradeOptions() {
    const gradeNos = this.getCurrentWorkingGradeNos()
    return gradeNos.map((item) => ({
      label: SchoolUtil.buildGradeName(item),
      value: item + '',
    }))
  }

  public static buildCurrentWorkingClassOptions(gradeNo: string | null) {
    if (!gradeNo) return []
    if (!this.currentWorkingCamp) return []
    const intGradeNo = parseInt(gradeNo + '')
    const clazzs = this.currentWorkingCamp.clazzs.filter((item) => item.grade_no === intGradeNo)
    const classNos = clazzs.map((item) => item.class_no)
    return classNos.map((item) => ({
      label: `${item}班`,
      value: item + '',
    }))
  }

  // 内容太长影响显示，只显示最后6位
  public static buildShowStudentEnrollmentNo(studentEnrollmentNo: string): string {
    if (studentEnrollmentNo.length > 15) {
      return '...' + studentEnrollmentNo.slice(-6)
    } else {
      return studentEnrollmentNo
    }
  }
}
